import React, { useState } from 'react'

interface ChangeRelease {
    version: string
    date: string
    title: string
    features: string[]
    bugFixes?: string[]
}

const changelogData: ChangeRelease[] = [
    {
        version: 'v1.5.3',
        date: 'Feb 28, 2026, 03:05 PM',
        title: 'EML Summarization Configurator',
        features: [
            'EML Configuration Parity: Unlocked the Literal Mode and Summarization Level configuration menus for EML (Newsletter) ingestions, matching Web Link parity.'
        ],
        bugFixes: []
    },
    {
        version: 'v1.5.2',
        date: 'Feb 28, 2026, 01:26 PM',
        title: 'Literal Mode Bounds Synthesizer',
        features: [],
        bugFixes: [
            'Google TTS Bounds Check: Replaced prosody-based Lookahead comma (`,`) injections with hard period (`.`) injections to natively circumvent Google TTS HTTP 400 string-length quotas on un-punctuated Literal Mode math and code blobs.'
        ]
    },
    {
        version: 'v1.5.1',
        date: 'Feb 28, 2026, 12:54 PM',
        title: 'AST Bracket-Balancing JSON Repair',
        features: [],
        bugFixes: [
            'Resilient JSON Pipeline: Replaced the legacy regex fallback with a mathematically sound AST bracket-balancing algorithm that rescues AI outputs truncated by quota limits.',
            'Fixed Edge Defaults: Handled edge cases where un-migrated Null max duration caps would instantly reject valid stories.',
            'Global SSML State Tracking: Engineered a 200-character global byte-tracker spanning XML node boundaries to inject prosody breaks safely.'
        ]
    },
    {
        version: 'v1.5.0',
        date: 'Feb 28, 2026, 09:15 AM',
        title: 'Literal Mode & Audio Telemetry',
        features: [
            'Literal Reading Mode: Bypass the LLM rewriting entirely for strictly verbatim narration of news and dev blogs.',
            'Custom ID3 Metadata Tags: Injects accurate Artist (Source Domain) and Title tags natively into downloaded MP3 files.',
            'Duration Telemetry: Embedded HTML5 audio player dynamically captures playing duration to calibrate frontend pacing meters.',
            'High Detail AI Mode: Instructs the generative parser to heavily elaborate and deeply explain complex articles.',
            'Deterministic Outro: Replaced AI hallucinated closings with mathematically bounded structured closings based on the Article Title.'
        ],
        bugFixes: []
    },
    {
        version: 'v1.4.0',
        date: 'Feb 27, 2026, 04:30 PM',
        title: 'Summarization Tuning & Previews',
        features: [
            'Added "Summarization Level" sliders to configure exactly how intensely the AI compresses stories (None, Low, Medium, High).',
            'Added text script previews to the UI review page. Ensure the parsing logic worked correctly before burning TTS compute credits.',
            'Token generation counters added heavily to track quota execution limits across complex scripts.'
        ],
        bugFixes: [
            'Optimized the mobile UI with responsive flexboxes and simplified nested grid cards.'
        ]
    },
    {
        version: 'v1.3.0',
        date: 'Feb 26, 2026, 02:45 PM',
        title: 'Universal Web Parsing',
        features: [
            'Direct Web URL Ingestion support! Paste any article or blog link to automatically digest it straight to audio.',
            'Moved from generic `.eml` processing boundaries to heuristic classification (recognizing News, Discussions, Multi-item roundups).'
        ],
        bugFixes: [
            'Implemented resilient parsing rules to intercept text chunks even if the underlying LLM truncates during analysis.'
        ]
    },
    {
        version: 'v1.2.0',
        date: 'Feb 25, 2026, 11:20 AM',
        title: 'Holistic Audio Engine',
        features: [
            'Consolidated per-story loose MP3 tracks into a single concatenated Episode Audio file.',
            'Built deterministic SSML bracket parsing ([PAUSE=...], [EMPH=...]) natively into Google Cloud boundaries.',
            'Chapter Marker implementations guaranteeing safe context switches.'
        ],
        bugFixes: []
    },
    {
        version: 'v1.1.0',
        date: 'Feb 24, 2026, 09:00 AM',
        title: 'Edge Architecture & Cloudflare',
        features: [
            'Full backend ecosystem migration to Cloudflare Pages (Frontend UI) and Cloudflare Workers (Backend Services).',
            'Data storage shifted to high-availability D1 (SQLite Edge Database) and R2 (Blob storage).',
            'Locked API and dashboard systems behind secure Admin Email Auth restrictions.'
        ],
        bugFixes: []
    }
]

type Tab = 'features' | 'bugs'

export default function Changelog() {
    const [activeTab, setActiveTab] = useState<Tab>('features')

    // Find the most recent date strings overall
    const latestGlobalDate = changelogData[0]?.date || ''

    // Filter releases based on active tab
    const displayedReleases = changelogData.filter(release => {
        if (activeTab === 'features') {
            return release.features && release.features.length > 0
        } else {
            return release.bugFixes && release.bugFixes.length > 0
        }
    })

    return (
        <div className="animate-fade-in" style={{ paddingBottom: '40px' }}>
            <div className="flex justify-between items-center mb-md">
                <h2>Changelog</h2>
            </div>

            <div className="text-sm text-secondary mb-lg italic" style={{ padding: '8px 12px', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <strong style={{ color: 'var(--text-primary)' }}>Most recent update:</strong> {latestGlobalDate}
            </div>

            <div className="flex gap-sm mb-lg" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '16px' }}>
                <button
                    className={`btn ${activeTab === 'features' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setActiveTab('features')}
                    style={{ flex: 1, padding: '12px', background: activeTab === 'features' ? '' : 'transparent', border: activeTab === 'features' ? 'none' : '1px solid var(--border-color)' }}
                >
                    ✨ Feature Additions
                </button>
                <button
                    className={`btn ${activeTab === 'bugs' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setActiveTab('bugs')}
                    style={{ flex: 1, padding: '12px', background: activeTab === 'bugs' ? '' : 'transparent', border: activeTab === 'bugs' ? 'none' : '1px solid var(--border-color)' }}
                >
                    🐛 Bug Fixes
                </button>
            </div>

            <div className="grid gap-md">
                {displayedReleases.map((release, i) => (
                    <div key={i} className="card flex-col gap-sm" style={{ position: 'relative' }}>
                        <div className="flex justify-between items-start flex-wrap gap-xs">
                            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className="text-secondary" style={{ fontSize: '0.8em', fontWeight: 'bold' }}>{release.version}</span>
                                {release.title}
                            </h3>
                            <span className="badge badge-neutral text-xs" style={{ whiteSpace: 'nowrap' }}>
                                {release.date}
                            </span>
                        </div>

                        {activeTab === 'features' && (
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: '8px' }} className="text-sm">
                                {release.features.map((feature, j) => (
                                    <li key={j} style={{ color: 'var(--text-secondary)' }}>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {activeTab === 'bugs' && release.bugFixes && (
                            <ul style={{ margin: '4px 0 0 0', paddingLeft: '1.2rem', display: 'flex', flexDirection: 'column', gap: '8px' }} className="text-sm">
                                {release.bugFixes.map((fix, j) => (
                                    <li key={j} style={{ color: 'var(--text-secondary)' }}>
                                        {fix}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ))}
            </div>

            {displayedReleases.length === 0 && (
                <div className="text-center text-secondary py-lg">
                    No historic data in this category.
                </div>
            )}

            <div className="text-center text-xs text-secondary mb-lg mt-lg">
                Tracking history for OpenCast Beta
            </div>
        </div>
    )
}
